import {
  BuildingOffice2Icon,
  EnvelopeIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import { memo, useMemo } from 'react';

import type { ReadPoliceDepartment } from '@/services/api/generated/policeDepartmentsService';
import { enhancedApi } from '@/services/api/policeDepartmentsService';
import { ContactHandleType } from '@/services/types/treatment-types';

import { createEntityLink } from '../core/createEntityLink';
import type { EntityTooltipContentProps } from '../core/types';
import {
  createCountBadge,
  createEnrichmentBadge,
  EmptyState,
  ErrorState,
  InfoRow,
  LoadingState,
  TooltipHeader,
  TooltipSection,
} from '../primitives';

/**
 * Police Department entity type (re-export from generated types)
 */
export type PoliceDepartment = ReadPoliceDepartment;

/**
 * Police Department tooltip content component
 * Pure presentation component - memoized for performance
 */
export const PoliceDepartmentTooltipContent = memo<
  EntityTooltipContentProps<PoliceDepartment>
>(({ fetchState }) => {
  // Compute derived values before early returns (Hooks must be called unconditionally)
  const department = fetchState.status === 'success' ? fetchState.data : null;

  const primaryEmail = useMemo(() => {
    return department?.handles?.find(
      (h) => h.handle_type === ContactHandleType.EMAIL,
    )?.value;
  }, [department?.handles]);

  const primaryPhone = useMemo(() => {
    return department?.handles?.find(
      (h) => h.handle_type === ContactHandleType.PHONE,
    )?.value;
  }, [department?.handles]);

  const location = useMemo(() => {
    if (!department) return null;
    const parts = [department.state_name, department.state_code]
      .filter(Boolean)
      .join(', ');
    return parts || null;
  }, [department]);

  // Handle loading state
  if (fetchState.status === 'pending') {
    return <LoadingState rows={2} />;
  }

  // Handle error state
  if (fetchState.status === 'error') {
    return (
      <ErrorState error={fetchState.error} entityType="police department" />
    );
  }

  // Handle idle or empty state - no data yet
  if (fetchState.status === 'idle' || !department) {
    return <EmptyState entityType="police department" />;
  }

  // Success state - render content
  const badges = (
    <div className="mb-2 flex flex-wrap gap-1.5">
      {createEnrichmentBadge(department.enrichment_status)}
      {createCountBadge(
        department.records_channels?.length ?? 0,
        'request method',
      )}
    </div>
  );

  return (
    <div className="divide-y divide-gray-100">
      <TooltipHeader
        icon={BuildingOffice2Icon}
        title={department.name ?? 'Police Department'}
      />

      <TooltipSection withBorder>
        {badges}
        {location && (
          <InfoRow
            icon={BuildingOffice2Icon}
            label="Location"
            value={location}
          />
        )}
        {(primaryEmail || primaryPhone) && (
          <>
            <InfoRow icon={EnvelopeIcon} label="Email" value={primaryEmail} />
            <InfoRow icon={PhoneIcon} label="Phone" value={primaryPhone} />
          </>
        )}
      </TooltipSection>

      {department.description && (
        <TooltipSection withBorder>
          <p className="line-clamp-3 text-sm leading-relaxed text-gray-600">
            {department.description}
          </p>
        </TooltipSection>
      )}
    </div>
  );
});

PoliceDepartmentTooltipContent.displayName = 'PoliceDepartmentTooltipContent';

/**
 * Hoverable police department link with tooltip preview
 *
 * Shows department name, location, enrichment status, contact info, and description on hover.
 * Displays badges for enrichment status and number of records request methods.
 * Click to navigate to police department detail page.
 *
 * @example
 * ```tsx
 * <PoliceDepartmentLink entityId={dept.id}>
 *   Los Angeles Police Department
 * </PoliceDepartmentLink>
 * ```
 */
export const PoliceDepartmentLink = createEntityLink<
  PoliceDepartment,
  { id: string }
>({
  tooltipContent: PoliceDepartmentTooltipContent,
  getDetailUrl: (id) => `/policedepartments/${id}`,
  useQuery: enhancedApi.endpoints.policeDepartmentsRetrieve.useQuery,
  getQueryArg: (id) => ({ id }),
  displayName: 'PoliceDepartmentLink',
});
